import { Centrifuge, UnauthorizedError, type Options, type Subscription } from 'centrifuge';
import { wantsJoinLeave } from './channels';
import { realtimeEndpoints } from './endpoints';
import { memberFromInfo, toWire, type ClientMessage, type Member } from './message';
import type { RealtimeConnection, TransportEvent } from './events';
import { RealtimeDeniedError, type RealtimeToken } from './token-api';

type Listener = (event: TransportEvent) => void;
type ChannelEntry = { listeners: Set<Listener>; sub: Subscription | null };

const MIN_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function retryDelay(attempt: number) {
  const base = Math.min(MAX_RETRY_MS, MIN_RETRY_MS * 2 ** attempt);
  return base / 2 + Math.random() * (base / 2);
}

function noop() {}

/**
 * One Centrifuge client for the tab. It is created lazily on `start()` because the WebSocket URL
 * comes back with the first token. Listeners registered before the client exists (or while it is
 * down) are attached when it comes up.
 */
export function createCentrifugeConnection(fetchToken: () => Promise<RealtimeToken>, tab: string): RealtimeConnection {
  const channels = new Map<string, ChannelEntry>();
  let client: Centrifuge | null = null;
  let clientId: string | null = null;
  // Bumped by start and stop so a token fetch that finishes after a stop is discarded.
  let generation = 0;

  function emit(channel: string, event: TransportEvent) {
    channels.get(channel)?.listeners.forEach((listener) => listener(event));
  }

  function attach(centrifuge: Centrifuge, channel: string, entry: ChannelEntry) {
    const sub = centrifuge.newSubscription(channel, { joinLeave: wantsJoinLeave(channel) });
    sub.on('publication', (ctx) => emit(channel, { kind: 'publication', data: ctx.data, info: ctx.info }));
    sub.on('join', (ctx) => emit(channel, { kind: 'join', info: ctx.info }));
    sub.on('leave', (ctx) => emit(channel, { kind: 'leave', info: ctx.info }));
    sub.on('subscribed', (ctx) =>
      emit(channel, { kind: 'subscribed', wasRecovering: ctx.wasRecovering, recovered: ctx.recovered }),
    );
    entry.sub = sub;
    sub.subscribe();
  }

  function detach(channel: string, entry: ChannelEntry) {
    channels.delete(channel);
    if (entry.sub === null) return;
    entry.sub.unsubscribe();
    client?.removeSubscription(entry.sub);
    entry.sub = null;
  }

  async function tokenWithRetry(gen: number): Promise<RealtimeToken | null> {
    for (let attempt = 0; gen === generation; attempt++) {
      try {
        return await fetchToken();
      } catch (error) {
        if (error instanceof RealtimeDeniedError) return null;
        await wait(retryDelay(attempt));
      }
    }
    return null;
  }

  async function refreshToken() {
    try {
      return (await fetchToken()).token;
    } catch (error) {
      // Denied is permanent: stop reconnecting. Anything else is retried by centrifuge-js.
      if (error instanceof RealtimeDeniedError) throw new UnauthorizedError(error.message);
      throw error;
    }
  }

  async function connect(gen: number) {
    const first = await tokenWithRetry(gen);
    if (first === null || gen !== generation) return;

    const { endpoints, emulationEndpoint } = realtimeEndpoints(first.url);
    // The server stamps the tab id into this connection's identity for everyone else to see.
    const options: Partial<Options> = { token: first.token, getToken: refreshToken, data: { tab } };
    if (emulationEndpoint !== undefined) options.emulationEndpoint = emulationEndpoint;
    const centrifuge = new Centrifuge(endpoints, options);
    client = centrifuge;
    centrifuge.on('connected', (ctx) => {
      clientId = ctx.client;
    });
    centrifuge.on('disconnected', () => {
      clientId = null;
    });
    channels.forEach((entry, channel) => attach(centrifuge, channel, entry));
    centrifuge.connect();
  }

  return {
    start() {
      generation += 1;
      void connect(generation);
    },

    stop() {
      generation += 1;
      client?.disconnect();
      client = null;
      clientId = null;
      channels.forEach((entry) => {
        entry.sub = null;
      });
    },

    subscribe(channel, listener) {
      let entry = channels.get(channel);
      if (entry === undefined) {
        entry = { listeners: new Set(), sub: null };
        channels.set(channel, entry);
        if (client !== null) attach(client, channel, entry);
      }
      entry.listeners.add(listener);

      const owner = entry;
      return () => {
        owner.listeners.delete(listener);
        if (owner.listeners.size === 0 && channels.get(channel) === owner) detach(channel, owner);
      };
    },

    publish(channel: string, message: ClientMessage) {
      channels.get(channel)?.sub?.publish(toWire(message)).catch(noop);
    },

    clientId: () => clientId,

    async rpc(method, request, parse) {
      if (client === null) throw new Error('realtime is not connected');
      const reply = parse((await client.rpc(method, request)).data);
      if (reply === null) throw new Error(`invalid ${method} reply`);
      return reply;
    },

    async presence(channel): Promise<Member[]> {
      const sub = channels.get(channel)?.sub;
      if (sub == null) return [];
      const { clients } = await sub.presence();
      return Object.values(clients).flatMap((info) => memberFromInfo(info) ?? []);
    },
  };
}
